import { useCallback, useState } from 'react';

export type UseDisclosureReturn = {
  /** Whether the disclosure is currently open */
  isOpen: boolean;
  /** Open the disclosure */
  open: () => void;
  /** Close the disclosure */
  close: () => void;
  /** Set the open state directly */
  setIsOpen: (isOpen: boolean) => void;
};

/**
 * Hook for managing disclosure state (modals, drawers, dropdowns, etc.)
 *
 * Provides methods to open, close, and toggle disclosure state.
 *
 * @param defaultOpen - Initial open state, defaults to false
 * @returns Object with isOpen state and control methods
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isOpen, open, close } = useDisclosure();
 *
 *   return (
 *     <>
 *       <Button onClick={open}>Open Modal</Button>
 *       <Modal isOpen={isOpen} onClose={close}>
 *         <p>Modal content</p>
 *       </Modal>
 *     </>
 *   );
 * }
 * ```
 */
export function useDisclosure(defaultOpen = false): UseDisclosureReturn {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    open,
    close,
    setIsOpen
  };
}
